import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCourse, uploadThumbnail, uploadEbook, uploadChapter, addModule, addLesson } from '../../services/api';
import { toast } from 'react-toastify';
import { FaBook, FaBookOpen, FaImage, FaFilePdf, FaPlus, FaTrash, FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CourseTypeSelector from '../../components/instructor/CourseTypeSelector.jsx';
import ModuleBuilder from '../../components/instructor/ModuleBuilder.jsx';
import CourseStructurePreview from '../../components/instructor/CourseStructurePreview.jsx';
import EbookStructurePreview from '../../components/instructor/EbookStructurePreview.jsx';
import './CreateCourse.css';

const CreateCourse = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [ebookFile, setEbookFile] = useState(null);
  const [selectedCourseType, setSelectedCourseType] = useState('video'); // 'video' or 'ebook'
  const [modules, setModules] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shortDescription: '',
    category: '',
    level: '',
    ebookSubcategory: '',
    videoSubcategory: '',
    indicatorName: '',
    price: 0,
    tags: '',
    requirements: '',
    learningObjectives: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCourseTypeChange = (type) => {
    setSelectedCourseType(type);
    if (type === 'ebook') {
      setModules([]); // Clear modules when switching to ebook
    } else {
      setChapters([]); // Clear chapters when switching to video
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setThumbnailFile(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const handleEbookChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please select a PDF file');
        return;
      }
      setEbookFile(file);
    }
  };

  const handleAddModule = () => {
    const newModule = {
      id: `module-${Date.now()}-${Math.random()}`,
      title: '',
      submodules: [],
      lessons: [],
      order: modules.length
    };
    setModules([...modules, newModule]);
  };

  const handleUpdateModule = (moduleId, updatedModule) => {
    setModules(modules.map(m => (m.id === moduleId ? updatedModule : m)));
  };

  const handleDeleteModule = (moduleId) => {
    setModules(modules.filter(m => m.id !== moduleId));
  };

  const handleModuleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(modules);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const reorderedModules = items.map((module, index) => ({
      ...module,
      order: index
    }));

    setModules(reorderedModules);
  };

  // Chapter handlers for ebooks
  const handleAddChapter = () => {
    const newChapter = {
      id: `chapter-${Date.now()}-${Math.random()}`,
      title: '',
      pdfFile: null,
      pageCount: 0,
      order: chapters.length
    };
    setChapters([...chapters, newChapter]);
  };

  const handleUpdateChapter = (chapterId, updatedChapter) => {
    setChapters(chapters.map(c => (c.id === chapterId ? updatedChapter : c)));
  };

  const handleDeleteChapter = (chapterId) => {
    setChapters(chapters.filter(c => c.id !== chapterId));
  };

  const handleChapterDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(chapters);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const reorderedChapters = items.map((chapter, index) => ({
      ...chapter,
      order: index
    }));

    setChapters(reorderedChapters);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Please enter a course description');
      return;
    }

    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }

    if (!formData.level) {
      toast.error('Please select a difficulty level');
      return;
    }

    // Type-specific validation
    if (selectedCourseType === 'ebook') {
      // Allow either single ebook file OR chapters
      if (!ebookFile && chapters.length === 0) {
        toast.error('Please upload a PDF file or add at least one chapter');
        return;
      }
      // Validate chapters have required fields
      if (chapters.length > 0) {
        const invalidChapters = chapters.filter(c => !c.title || !c.pdfFile);
        if (invalidChapters.length > 0) {
          toast.error('All chapters must have a title and PDF file');
          return;
        }
      }
    } else if (selectedCourseType === 'video') {
      if (modules.length === 0) {
        toast.error('Please add at least one module for the video course');
        return;
      }

      // Validate modules have content
      const hasContent = modules.some(m =>
        (m.lessons && m.lessons.length > 0) ||
        (m.submodules && m.submodules.length > 0)
      );

      if (!hasContent) {
        toast.error('Please add lessons or submodules to your modules');
        return;
      }
    }

    setLoading(true);

    try {
      // Prepare course data
      const courseData = {
        title: formData.title,
        description: formData.description,
        shortDescription: formData.shortDescription,
        category: formData.category,
        courseType: selectedCourseType === 'ebook' ? 'ebook' : 'video',
        level: formData.level,
        price: parseFloat(formData.price) || 0,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        requirements: formData.requirements.split('\n').filter(req => req.trim()),
        learningObjectives: formData.learningObjectives.split('\n').filter(obj => obj.trim())
      };

      // Add ebook-specific data (Only for Ebook Category)
      if (selectedCourseType === 'ebook' && formData.category === 'ebook') {
        courseData.ebookSubcategory = formData.ebookSubcategory;
      }

      // Add video-specific data for tradingview category (Works for Ebooks too now)
      if (formData.category === 'tradingview') {
        courseData.videoSubcategory = formData.videoSubcategory;
      }

      // Add SISTEM-specific data (Video OR Ebook)
      if (formData.category === 'sistem') {
        courseData.videoSubcategory = formData.videoSubcategory;
        if (formData.videoSubcategory === 'indicator') {
          courseData.indicatorName = formData.indicatorName;
        }
      }

      // Create course
      const courseRes = await createCourse(courseData);
      const courseId = courseRes.data.course._id;

      // Upload thumbnail if provided
      if (thumbnailFile) {
        const thumbnailFormData = new FormData();
        thumbnailFormData.append('thumbnail', thumbnailFile);
        await uploadThumbnail(courseId, thumbnailFormData);
      }

      // Handle ebook upload (legacy single-file upload)
      if (selectedCourseType === 'ebook' && ebookFile) {
        const ebookFormData = new FormData();
        ebookFormData.append('ebook', ebookFile);
        await uploadEbook(courseId, ebookFormData);
      }

      // Handle multi-chapter ebook upload
      if (selectedCourseType === 'ebook' && chapters.length > 0) {
        for (const chapter of chapters) {
          const chapterFormData = new FormData();
          chapterFormData.append('chapter', chapter.pdfFile);
          chapterFormData.append('title', chapter.title);
          chapterFormData.append('pageCount', chapter.pageCount || 0);
          chapterFormData.append('order', chapter.order + 1);
          await uploadChapter(courseId, chapterFormData);
        }
      }

      // Handle video course structure and uploads
      if (selectedCourseType === 'video') {
        // Create modules, submodules, and lessons structure
        for (const module of modules) {
          const moduleData = {
            title: module.title,
            order: module.order
          };

          // Create module
          const moduleRes = await addModule(courseId, moduleData);
          const createdModuleId = moduleRes.data.module._id;

          // Create submodules and their lessons
          if (module.submodules && module.submodules.length > 0) {
            for (const submodule of module.submodules) {
              // In a full implementation, you'd need a backend API for submodules
              // For now, we'll treat submodules as nested structure

              // Create lessons in submodules
              if (submodule.lessons) {
                for (const lesson of submodule.lessons) {
                  const lessonData = {
                    title: lesson.title,
                    duration: lesson.duration,
                    order: lesson.order,
                    type: 'video',
                    videoUrl: lesson.videoUrl || ''
                  };

                  // Create lesson with YouTube URL
                  await addLesson(courseId, createdModuleId, lessonData);
                }
              }
            }
          }

          // Create direct module lessons
          if (module.lessons && module.lessons.length > 0) {
            for (const lesson of module.lessons) {
              const lessonData = {
                title: lesson.title,
                duration: lesson.duration,
                order: lesson.order,
                type: 'video',
                videoUrl: lesson.videoUrl || ''
              };

              // Create lesson with YouTube URL
              await addLesson(courseId, createdModuleId, lessonData);
            }
          }
        }
      }

      toast.success(
        selectedCourseType === 'ebook'
          ? 'Ebook created successfully!'
          : 'Video course created successfully!'
      );

      navigate(`/instructor/courses`);
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error(error.response?.data?.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  const isEbook = selectedCourseType === 'ebook';

  return (
    <div className="create-course-page">
      <div className="container">
        <div className="create-course-header">
          <h1>
            {isEbook ? <FaBookOpen /> : <FaBook />}
            Create New {isEbook ? 'Ebook' : 'Video Course'}
          </h1>
          <p>Fill in the details below to create your {isEbook ? 'ebook' : 'course'}</p>
        </div>

        <form onSubmit={handleSubmit} className="create-course-form">
          {/* Course Type Selection */}
          <div className="form-section">
            <CourseTypeSelector
              selectedType={selectedCourseType}
              onChange={handleCourseTypeChange}
            />
          </div>

          {/* Basic Information */}
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter course title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="shortDescription">Short Description</label>
              <textarea
                id="shortDescription"
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleInputChange}
                placeholder="Brief description for course cards (max 300 characters)"
                maxLength={300}
                rows={2}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Full Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Detailed course description"
                rows={6}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Category</option>
                  {selectedCourseType === 'video' ? (
                    <>
                      <option value="tradingview">Tradingview</option>
                      <option value="indicator">Indicator</option>
                      <option value="sistem">SISTEM</option>
                      <option value="general">General</option>
                    </>
                  ) : (
                    <>
                      <option value="ebook">Ebook</option>
                      <option value="tradingview">Tradingview</option>
                      <option value="sistem">SISTEM</option>
                    </>
                  )}
                </select>
              </div>

              {isEbook && formData.category === 'ebook' && (
                <div className="form-group">
                  <label htmlFor="ebookSubcategory">Ebook Level</label>
                  <select
                    id="ebookSubcategory"
                    name="ebookSubcategory"
                    value={formData.ebookSubcategory}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Ebook Level</option>
                    <option value="basic">Basic</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              )}

              {formData.category === 'sistem' && (
                <div className="form-group">
                  <label htmlFor="videoSubcategory">SISTEM Subcategory *</label>
                  <select
                    id="videoSubcategory"
                    name="videoSubcategory"
                    value={formData.videoSubcategory}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Subcategory</option>
                    <option value="indicator">Indicator</option>
                    <option value="ea-copier">EA Copier</option>
                  </select>
                </div>
              )}

              {formData.category === 'sistem' && formData.videoSubcategory === 'indicator' && (
                <div className="form-group">
                  <label htmlFor="indicatorName">Indicator Name *</label>
                  <input
                    type="text"
                    id="indicatorName"
                    name="indicatorName"
                    value={formData.indicatorName || ''}
                    onChange={handleInputChange}
                    placeholder="e.g., SFT, OMS, STE, GOOGEM"
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                    Nama indicator untuk display dalam SISTEM page
                  </small>
                </div>
              )}

              {formData.category === 'tradingview' && (
                <div className="form-group">
                  <label htmlFor="videoSubcategory">Subcategory *</label>
                  <select
                    id="videoSubcategory"
                    name="videoSubcategory"
                    value={formData.videoSubcategory}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Subcategory</option>
                    <option value="handphone">Modul Handphone</option>
                    <option value="laptop">Modul Laptop</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="level">Difficulty Level *</label>
                <select
                  id="level"
                  name="level"
                  value={formData.level}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Level</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="price">Price ($)</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="e.g., forex, stocks, day trading"
              />
            </div>
          </div>

          {/* Media */}
          <div className="form-section">
            <h2>Media</h2>

            <div className="form-group">
              <label htmlFor="thumbnail">
                <FaImage /> Thumbnail Image
              </label>
              <div className="file-upload-area">
                {thumbnailPreview ? (
                  <div className="image-preview">
                    <img src={thumbnailPreview} alt="Thumbnail preview" />
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview(null);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label htmlFor="thumbnail" className="file-upload-label">
                    <FaImage size={48} />
                    <span>Click to upload thumbnail</span>
                    <small>PNG, JPG, GIF up to 10MB</small>
                  </label>
                )}
                <input
                  type="file"
                  id="thumbnail"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {isEbook && (
              <div className="form-group">
                <label htmlFor="ebookPdf">
                  <FaFilePdf /> Ebook PDF File (Single File)
                </label>
                <div className="file-upload-area">
                  {ebookFile ? (
                    <div className="file-selected">
                      <FaFilePdf size={48} color="#dc2626" />
                      <span>{ebookFile.name}</span>
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => setEbookFile(null)}
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="ebookPdf" className="file-upload-label">
                      <FaFilePdf size={48} />
                      <span>Click to upload PDF</span>
                      <small>PDF file up to 10MB</small>
                    </label>
                  )}
                  <input
                    type="file"
                    id="ebookPdf"
                    accept="application/pdf"
                    onChange={handleEbookChange}
                    style={{ display: 'none' }}
                  />
                </div>
                <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                  Upload a single PDF file, or use the Chapters section below for multi-chapter ebooks.
                </small>
              </div>
            )}
          </div>

          {/* Ebook Chapters */}
          {isEbook && (
            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Chapters (Optional)</h2>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddChapter}
                >
                  <FaPlus /> Add Chapter
                </button>
              </div>

              {chapters.length > 0 ? (
                <>
                  <DragDropContext onDragEnd={handleChapterDragEnd}>
                    <Droppable droppableId="chapters">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {chapters.map((chapter, index) => (
                            <Draggable key={chapter.id} draggableId={chapter.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className="chapter-builder-item"
                                  style={{
                                    marginBottom: '1rem',
                                    padding: '1.5rem',
                                    background: 'var(--bg-secondary)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-color)',
                                    ...provided.draggableProps.style
                                  }}
                                >
                                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                    <div
                                      {...provided.dragHandleProps}
                                      style={{
                                        padding: '0.5rem',
                                        cursor: 'grab',
                                        color: 'var(--text-secondary)'
                                      }}
                                    >
                                      <FaGripVertical />
                                    </div>

                                    <div style={{ flex: 1 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h4 style={{ margin: 0 }}>Chapter {index + 1}</h4>
                                        <button
                                          type="button"
                                          className="btn-icon btn-danger"
                                          onClick={() => handleDeleteChapter(chapter.id)}
                                          title="Delete chapter"
                                        >
                                          <FaTrash />
                                        </button>
                                      </div>

                                      <div className="form-group">
                                        <label>Chapter Title *</label>
                                        <input
                                          type="text"
                                          value={chapter.title}
                                          onChange={(e) =>
                                            handleUpdateChapter(chapter.id, {
                                              ...chapter,
                                              title: e.target.value
                                            })
                                          }
                                          placeholder="e.g., Introduction to Trading"
                                          required
                                        />
                                      </div>

                                      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
                                        <div className="form-group">
                                          <label>PDF File *</label>
                                          <input
                                            type="file"
                                            accept=".pdf"
                                            onChange={(e) =>
                                              handleUpdateChapter(chapter.id, {
                                                ...chapter,
                                                pdfFile: e.target.files[0]
                                              })
                                            }
                                            required
                                          />
                                          {chapter.pdfFile && (
                                            <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                                              <FaFilePdf /> {chapter.pdfFile.name}
                                            </small>
                                          )}
                                        </div>

                                        <div className="form-group">
                                          <label>Page Count (Optional)</label>
                                          <input
                                            type="number"
                                            value={chapter.pageCount}
                                            onChange={(e) =>
                                              handleUpdateChapter(chapter.id, {
                                                ...chapter,
                                                pageCount: parseInt(e.target.value) || 0
                                              })
                                            }
                                            placeholder="0"
                                            min="0"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {/* Ebook Structure Preview */}
                  <div style={{ marginTop: '2rem' }}>
                    <EbookStructurePreview chapters={chapters} />
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>No chapters added yet. Click "Add Chapter" to create your first chapter, or upload a single PDF above.</p>
                </div>
              )}
            </div>
          )}

          {/* Video Course Content */}
          {!isEbook && (
            <div className="form-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>Course Content</h2>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddModule}
                >
                  <FaPlus /> Add Module
                </button>
              </div>

              {modules.length > 0 ? (
                <DragDropContext onDragEnd={handleModuleDragEnd}>
                  <Droppable droppableId="modules">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {modules.map((module, index) => (
                          <Draggable key={module.id} draggableId={module.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <ModuleBuilder
                                  module={module}
                                  index={index}
                                  onUpdate={(updatedModule) => handleUpdateModule(module.id, updatedModule)}
                                  onDelete={() => handleDeleteModule(module.id)}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div className="empty-state">
                  <p>No modules added yet. Click "Add Module" to create your first module.</p>
                </div>
              )}

              {/* Course Structure Preview */}
              {modules.length > 0 && (
                <CourseStructurePreview modules={modules} />
              )}
            </div>
          )}

          {/* Additional Details */}
          <div className="form-section">
            <h2>Additional Details</h2>

            <div className="form-group">
              <label htmlFor="learningObjectives">Learning Objectives (one per line)</label>
              <textarea
                id="learningObjectives"
                name="learningObjectives"
                value={formData.learningObjectives}
                onChange={handleInputChange}
                placeholder="What will students learn?"
                rows={5}
              />
            </div>

            <div className="form-group">
              <label htmlFor="requirements">Requirements (one per line)</label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleInputChange}
                placeholder="What do students need to know beforehand?"
                rows={4}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/instructor/courses')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : isEbook ? 'Create Ebook' : 'Create Video Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCourse;
